<template>
  <TSettingsBlock
    data-test="TCourseCreate-Colors"
    :title="$t('course_settings.details.title')"
    :sub-title="$t('course_settings.details.sub_title')"
    :force-open="forceOpen"
  >
    <div v-if="fetching">
      <TLoadingIndicatorBig />
    </div>
    <div v-else-if="error">
      <TCalloutErrorGeneric />
    </div>
    <div v-else>
      <TH2 class="mb-3">{{ $t('course_name') }}</TH2>

      <TFormRowTextInput
        id="coursename"
        v-model="newName"
        type="text"
        :has-error="formRenameHasErrors"
        :error-message="formRenameError"
        :placeholder="$t('library.create_course.placeholder_name_course')"
        data-test-name="coursename"
        autocomplete="off"
      />

      <TH2 class="mb-3 mt-6">{{ $t('course_short_name') }}</TH2>

      <TFormRowTextInput
        id="courseShortName"
        v-model="newShortName"
        type="text"
        :has-error="formShortNameHasErrors"
        :error-message="formShortNameError"
        :placeholder="$t('library.create_course.placeholder_short_name_course')"
        data-test-name="courseshortname "
        autocomplete="off"
      />

      <template v-if="!isTemplate()">
        <TH2 class="mb-3 mt-6">{{ $t('generic_schoolyear') }}</TH2>

        <TFormRowDropDown
          id="schoolyear"
          v-model="selectedSchoolYear"
          :items="data.tenantEditableSchoolYears"
          label=""
          text-identifier="name"
          value-identifier="id"
          :has-error="formSchoolYearHasError"
          data-test-name="Schoolyear"
        />
      </template>

      <template
        v-if="course.permissions.canEditCourseSettings && !isTemplate()"
      >
        <TH2 class="mb-3 mt-6">{{ $t('course_create_color') }}</TH2>

        <div>
          <!--          color picker-->
          <CourseSettingsColorPicker
            :selected-course-colors="selectedCourseColors"
            :update-course-colors="updateCourseColors"
            :pre-selected-color="courseColorsFromApi"
          />
        </div>
      </template>

      <TButtonInline
        class="mb-8 mt-4"
        :action="save"
        :loading="loading"
        :disabled="!buttonActive"
      >
        {{ $t('generic_save') }}
      </TButtonInline>
    </div>
  </TSettingsBlock>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  COURSE_NAME_CHAR_LIMIT,
  SHORT_NAME_CHAR_LIMIT,
} from '~/utils/constants';
import { type CourseColor } from '~/utils/colorConstants';
import toast from '~/components/Toast';
import { type Course, NewCourseDataDocument, UsageType } from '~/graphql';
import { useCourseManagement } from '~/stores/courseManagement';
import { useSession } from '~/stores/session';

import { useQuery } from '@urql/vue';
import {
  TCalloutErrorGeneric,
  TLoadingIndicatorBig,
} from '@teachino/shared-design-system';

const props = defineProps<{ course: Course; forceOpen?: boolean }>();
const prevName = ref('');
const newName = ref('');
const newShortName = ref('');

const { $t } = useNuxtApp();

const courseManagementStore = useCourseManagement();
courseManagementStore.init();

// Rename course
const formRenameHasErrors = ref(false);
const formShortNameHasErrors = ref(true);
const buttonActive = computed(() => {
  return newName.value !== '' && newShortName.value !== '';
});
const formRenameError = ref();
const formShortNameError = ref('');
// watch if modelValue exceeds characterlimit and update the internalErrorMessage
watch(newName, (value) => {
  if (value.length > COURSE_NAME_CHAR_LIMIT) {
    formRenameHasErrors.value = true;
    formRenameError.value = $t('course_create_char_limit_error', [
      { id: 'limit', value: COURSE_NAME_CHAR_LIMIT },
    ]);
  } else if (value.length === 0) {
    formRenameHasErrors.value = true;
    formRenameError.value = $t('course_create_name_required');
  } else {
    formRenameHasErrors.value = false;
    formRenameError.value = '';
  }
});

watch(newShortName, (value) => {
  if (value?.length > SHORT_NAME_CHAR_LIMIT) {
    formShortNameHasErrors.value = true;
    formShortNameError.value = $t('course_create_short_name_char_limit_error', [
      { id: 'limit', value: SHORT_NAME_CHAR_LIMIT },
    ]);
  } else {
    formShortNameHasErrors.value = false;
    formShortNameError.value = '';
  }
});

// Course colors
const selectedCourseColors = ref<CourseColor | null>(null);
const courseColorsFromApi = ref<CourseColor | null>(null);

const updateCourseColors = (color: CourseColor) => {
  selectedCourseColors.value = color;
};

const loading = ref(false);
async function save() {
  if (formShortNameHasErrors.value) {
    return;
  }
  if (formRenameHasErrors.value) {
    return;
  }

  loading.value = true;

  try {
    await courseManagementStore.updateCourseDetails(props.course.id as string, {
      shortName: newShortName.value,
      name: newName.value,
      foregroundColor: selectedCourseColors.value.foreground,
      backgroundColor: selectedCourseColors.value.background,
      schoolYearId:
        selectedSchoolYear.value !== '' ? selectedSchoolYear.value : undefined,
    });
    toast.success($t('success'));
  } catch (e) {
    console.log(e);
    toast.error($t('generic_error'));
  }

  loading.value = false;
}

function isTemplate() {
  return props.course.usage === UsageType.Template ? true : false;
}

// schoolyear
const sessionStore = useSession();
const formSchoolYearHasError = ref(false);
const { fetching, data, error } = useQuery({
  query: NewCourseDataDocument,
  variables: {
    tenantIdInput: {
      id: sessionStore.getDefaultTenantId,
    },
    coursesQueryfilter: {
      status: 'OPEN',
    },
  },
});

const selectedSchoolYear = ref<string>('');

watch(
  () => props.course,
  (value) => {
    newName.value = props.course?.name as string;
    prevName.value = props.course?.name as string;
    newShortName.value = props.course?.shortName as string;
    const color = {
      foreground: props.course?.foregroundColor as string,
      background: props.course?.backgroundColor as string,
    };

    selectedCourseColors.value = color;
    courseColorsFromApi.value = color;
    if (props.course?.schoolYear) {
      selectedSchoolYear.value = props.course?.schoolYear.id as string;
    }
  },
  { immediate: true },
);
</script>
